import {
  compileFreeformFilterBlock,
  countActiveFreeformFilterBlocks,
  deepEqual,
  type FreeformFilterBlock,
} from "@tellescope/utils";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { InboxView } from "../../../types/inbox";

export type InboxReadStatusFilter = "all" | "read" | "unread";

/** Whether to show every thread the user can see, or only those assigned to them. */
export type InboxOwnershipFilter = "all" | "mine";

export type InboxChannel = TellescopeModelFromName<"inbox_threads">["type"];

export interface InboxDateRangeFilter {
  from?: number;
  to?: number;
}

export interface InboxFilters {
  ownership?: InboxOwnershipFilter;
  readStatus?: InboxReadStatusFilter;
  inboxStatus?: string[];
  channels?: InboxChannel[];
  assignees?: string[];
  /** Users covering the thread's patients — matched against the thread's `userIds` coverage list. */
  careTeamIds?: string[];
  patients?: string[];
  tags?: string[];
  dateRange?: InboxDateRangeFilter;
  customBlocks?: FreeformFilterBlock[];
}

export const DEFAULT_INBOX_FILTERS: InboxFilters = {};

/**
 * Channels hidden unless the user explicitly asks for them: phone calls and group MMS are surfaced
 * through their own views, not the main thread list.
 */
const IMPLICITLY_EXCLUDED_CHANNELS: InboxChannel[] = ["Phone", "GroupMMS"];

/**
 * Compiles the user-selected inbox filters into a MongoDB-style filter for `load_threads`.
 *
 * Only user filters are compiled. Route-derived view scoping (the archived/trashed clauses that
 * distinguish Inbox from Archive and Trash) is deliberately excluded and combined by the caller — a
 * saved view must not carry scoping, or a view saved from Archive would show archived threads in the
 * Inbox route.
 *
 * @param filters the active filters; `undefined` compiles to the unfiltered default
 * @param currentUserId needed for read/unread, which resolves to a per-user `readBy.<id>` key
 */
export const buildInboxMdbFilter = (
  filters: InboxFilters | undefined,
  currentUserId: string | undefined
): Record<string, unknown> => {
  const top: Record<string, unknown> = {};
  const andClauses: Record<string, unknown>[] = [];

  // Absent selection still constrains the query: the excluded channels stay hidden by default.
  top.type = filters?.channels?.length ? { $in: filters.channels } : { $nin: IMPLICITLY_EXCLUDED_CHANNELS };

  if (!filters) return top;

  if (filters.readStatus === "unread" && currentUserId) {
    top[`readBy.${currentUserId}`] = { $in: [null, ""] };
  } else if (filters.readStatus === "read" && currentUserId) {
    top[`readBy.${currentUserId}`] = { $nin: [null, ""] };
  }

  if (filters.inboxStatus?.length) {
    top.inboxStatus = { $in: filters.inboxStatus };
  }

  if (filters.tags?.length) {
    top.tags = { $elemMatch: { $in: filters.tags } };
  }

  if (filters.ownership === "mine" && currentUserId) {
    // A thread with no explicit assignee falls to the patient's care team, so "mine" has to include
    // those too — otherwise unassigned threads the user is responsible for disappear. Legacy patches
    // this client-side (`filter_by_mine`); compiling it as an $or keeps it server-side.
    andClauses.push({
      $or: [
        { assignedTo: { $elemMatch: { $in: [currentUserId] } } },
        { assignedTo: { $in: [null, []] }, userIds: { $elemMatch: { $in: [currentUserId] } } },
      ],
    });
  }

  if (filters.assignees?.length) {
    top.assignedTo = { $elemMatch: { $in: filters.assignees } };
  }

  if (filters.patients?.length) {
    top.enduserIds = { $elemMatch: { $in: filters.patients } };
  }

  const timestampBounds = buildTimestampBounds(filters.dateRange);
  if (timestampBounds) {
    top.timestamp = timestampBounds;
  }

  filters.customBlocks?.forEach((block) => {
    const compiled = compileFreeformFilterBlock(block);
    if (compiled && Object.keys(compiled).length) {
      andClauses.push(compiled);
    }
  });

  if (andClauses.length) {
    top.$and = andClauses;
  }

  return top;
};

const buildTimestampBounds = (dateRange: InboxDateRangeFilter | undefined): Record<string, Date> | undefined => {
  if (dateRange?.from === undefined && dateRange?.to === undefined) return undefined;

  const bounds: Record<string, Date> = {};
  if (dateRange.from !== undefined) bounds.$gte = new Date(dateRange.from);
  if (dateRange.to !== undefined) bounds.$lte = new Date(dateRange.to);
  return bounds;
};

/**
 * Compiles the filter behind the navbar's unhandled-thread count.
 *
 * The badge answers "how many threads still need me" for the inbox the user would actually open, so
 * it is the active view's filters, scoped to the Inbox route, plus a `New` status constraint.
 *
 * `New` is ANDed rather than assigned over the view's own status selection: a view filtered to
 * Resolved must count zero, not silently fall back to counting New.
 */
export const buildInboxUnreadCountMdbFilter = (
  filters: InboxFilters | undefined,
  currentUserId: string | undefined
): Record<string, unknown> => {
  const { inboxStatus, $and: viewAndClauses, ...rest } = buildInboxMdbFilter(filters, currentUserId);

  return {
    ...buildInboxViewScopeFilter("inbox"),
    ...rest,
    $and: [
      ...(Array.isArray(viewAndClauses) ? viewAndClauses : []),
      ...(inboxStatus === undefined ? [] : [{ inboxStatus }]),
      { inboxStatus: "New" },
    ],
  };
};

/**
 * Compiles the archived/trashed scoping that distinguishes the Inbox, Archive and Trash routes.
 *
 * Kept separate from {@link buildInboxMdbFilter} because this is a property of the route the user is
 * on, not of their filter selection — a saved view must never carry it.
 */
export const buildInboxViewScopeFilter = (view: InboxView): Record<string, unknown> => {
  if (view === "archive") return { archivedAt: { $ne: "" }, trashedAt: "" };
  if (view === "trash") return { trashedAt: { $ne: "" }, archivedAt: "" };
  return { archivedAt: "", trashedAt: "" };
};

/** Counts active filter groups, for the filter badge and the dialog's dirty affordances. */
export const countActiveInboxFilters = (filters: InboxFilters | undefined): number => {
  if (!filters) return 0;

  let count = 0;
  if (filters.ownership && filters.ownership !== "all") count++;
  if (filters.careTeamIds?.length) count++;
  if (filters.readStatus && filters.readStatus !== "all") count++;
  if (filters.inboxStatus?.length) count++;
  if (filters.channels?.length) count++;
  if (filters.assignees?.length) count++;
  if (filters.patients?.length) count++;
  if (filters.tags?.length) count++;
  if (filters.dateRange?.from !== undefined || filters.dateRange?.to !== undefined) count++;
  count += countActiveFreeformFilterBlocks(filters.customBlocks);
  return count;
};

/** True when two filter sets are equivalent, for staged-vs-applied dirty checks. */
export const inboxFiltersAreEqual = (a: InboxFilters, b: InboxFilters): boolean =>
  deepEqual(a, b, { includeRemovals: true });
